import { z } from "zod";
import {
  MEMBER_DUE_STATUS,
  PAYMENT_CONFIRMATION_STATUS,
} from "./collections.js";
// US-E16.F6-0187 (@UC9249): chính sách overpay hiệu lực trả kèm response record-paid (consumer THẬT).
import { overpayCreditPolicySchema } from "./collection-config.js";
// US-E19.F1-0200 (@UC9296): khoá idempotency NGHIỆP VỤ (1 nguồn — dùng chung record-paid + correction).
import { businessIdempotencyKeySchema } from "./ledger-correction.js";

/* ===========================================================================
 * US-E6.F3-0041 — Approve / Decline Payment Confirmation (@UC0040 / @UC0051)
 * Phase 6 Ledger core + Phase 7 Payments — @gate money,ledger.
 *
 * REST (PO-chốt 01/07): resource-oriented trên payment_confirmation.
 *  - GET  /v1/collections/:id/dues?status=pending      — list báo CK chờ duyệt (cursor)
 *  - POST /v1/payment-confirmations/:id/approve         — duyệt → post CONTRIBUTION + due=paid
 *  - POST /v1/payment-confirmations/:id/decline         — từ chối (reason) → due=pending, KHÔNG ledger
 *
 * B1: mọi số tiền = STRING minor unit (BigInt, KHÔNG float).
 * ========================================================================= */

/** Chuỗi số nguyên minor unit (BigInt-as-STRING) — chống mất chính xác >2^53. */
const MONEY_INTEGER_STRING_PATTERN = /^\d+$/;

/** confirmationSource cho CONTRIBUTION (khớp LedgerTransaction.confirmationSource). */
export const CONFIRMATION_SOURCE = {
  TWO_PARTY: "two_party",
  ADMIN_ONE_PARTY: "admin_one_party",
  GATEWAY_AUTO: "gateway_auto",
  /**
   * AI (US-E11.F1-0064-S03 · @UC0072): bút toán do trợ lý AI tạo SAU khi người dùng Xác nhận
   * proposal (human-in-the-loop). Tag này để admin ai-audit trace nối idempotencyKey chat→ledger.
   * AI KHÔNG BAO GIỜ ghi ledger trực tiếp — chỉ qua domain engine (LedgerService/consensus/collection).
   */
  AI: "ai",
} as const;

export type ConfirmationSource =
  (typeof CONFIRMATION_SOURCE)[keyof typeof CONFIRMATION_SOURCE];

export const confirmationSourceSchema = z.enum([
  CONFIRMATION_SOURCE.TWO_PARTY,
  CONFIRMATION_SOURCE.ADMIN_ONE_PARTY,
  CONFIRMATION_SOURCE.GATEWAY_AUTO,
  CONFIRMATION_SOURCE.AI,
]);

/* ---------------------------------------------------------------------------
 * GET /v1/collections/:id/dues?status=pending — danh sách báo CK chờ duyệt (AC1)
 * ------------------------------------------------------------------------- */

export const pendingDueItemSchema = z
  .object({
    confirmationId: z.string().uuid(),
    memberDueId: z.string().uuid(),
    memberId: z.string().uuid(),
    memberName: z.string().nullable(),
    claimedAmountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    currency: z.string().length(3),
    proofUrl: z.string().url().nullable(),
    submittedAt: z.string().datetime(),
  })
  .strict();

export type PendingDueItem = z.infer<typeof pendingDueItemSchema>;

export const listPendingDuesResponseSchema = z
  .object({
    dues: z.array(pendingDueItemSchema),
    nextCursor: z.string().nullable(),
  })
  .strict();

export type ListPendingDuesResponse = z.infer<
  typeof listPendingDuesResponseSchema
>;

/* ---------------------------------------------------------------------------
 * POST /v1/payment-confirmations/:id/approve — duyệt (AC2)
 * ------------------------------------------------------------------------- */

export const approvePaymentResponseSchema = z
  .object({
    confirmationId: z.string().uuid(),
    memberDueId: z.string().uuid(),
    ledgerTxId: z.string().uuid(),
    confirmationSource: confirmationSourceSchema,
    amountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    currency: z.string().length(3),
    newDueStatus: z.literal(MEMBER_DUE_STATUS.PAID),
    newConfirmationStatus: z.literal(PAYMENT_CONFIRMATION_STATUS.APPROVED),
    approvedAt: z.string().datetime(),
  })
  .strict();

export type ApprovePaymentResponse = z.infer<
  typeof approvePaymentResponseSchema
>;

/* ---------------------------------------------------------------------------
 * POST /v1/payment-confirmations/:id/decline — từ chối + reason (AC3)
 * ------------------------------------------------------------------------- */

/** Reason bắt buộc (>=1 ký tự sau trim), tối đa 500 để chống abuse. */
export const declinePaymentRequestSchema = z
  .object({
    reason: z.string().trim().min(1).max(500),
  })
  .strict();

export type DeclinePaymentRequest = z.infer<
  typeof declinePaymentRequestSchema
>;

export const declinePaymentResponseSchema = z
  .object({
    confirmationId: z.string().uuid(),
    memberDueId: z.string().uuid(),
    newDueStatus: z.literal(MEMBER_DUE_STATUS.PENDING),
    newConfirmationStatus: z.literal(PAYMENT_CONFIRMATION_STATUS.REJECTED),
    reason: z.string(),
    declinedAt: z.string().datetime(),
  })
  .strict();

export type DeclinePaymentResponse = z.infer<
  typeof declinePaymentResponseSchema
>;

/* ===========================================================================
 * US-E6.F3-0042 — Record-paid (admin_one_party) + bulk + undo (@UC0040)
 * Phase 6 Ledger + Phase 7 — @gate money,ledger. REUSE móng LedgerService 0041.
 * ========================================================================= */

/** Trần PG signed BIGINT (chống overflow P2020→500). */
const RECORD_PAID_PG_BIGINT_MAX = 9223372036854775807n;

/**
 * Body cho POST /v1/member-dues/:id/record-paid.
 *  - `declaredAmountMinor?` (US-E16.F1-0182 · @UC9234 open_amount): CHỈ dùng khi due là open_amount
 *    (amount=0 lúc tạo) — member/admin KHAI số thực đóng. Đợt equal/per_member/tiered/target_split
 *    (due amount>0) BỎ QUA field này (dùng due.amountMinor — hành vi cũ KHÔNG regress).
 *  - STRING số nguyên dương ≤ trần BIGINT (B1 — KHÔNG float). `.strict()` chặn over-post.
 */
const positiveMinorString = (label: string) =>
  z
    .string()
    .trim()
    .regex(MONEY_INTEGER_STRING_PATTERN, `${label} phải là số nguyên (đơn vị nhỏ nhất)`)
    .refine((v) => {
      try {
        const n = BigInt(v);
        return n > 0n && n <= RECORD_PAID_PG_BIGINT_MAX;
      } catch {
        return false;
      }
    }, `${label} phải lớn hơn 0 và không vượt giới hạn`);

export const recordPaidRequestSchema = z
  .object({
    declaredAmountMinor: positiveMinorString("Số tiền").optional(),
    /**
     * US-E16.F4-0185 (@UC9241/@UC9242): số THỰC đóng cho due amount>0 (partial/overpay).
     *  - < còn-nợ ⇒ đóng MỘT PHẦN (due `partially_paid`, còn nợ = due − paid).
     *  - = còn-nợ ⇒ đóng ĐỦ (due `paid`) — tương đương hành vi cũ (KHÔNG regress).
     *  - > còn-nợ ⇒ đóng DƯ (due `paid` + phần dư → advance_credit producer E15.F3).
     * Vắng ⇒ đóng ĐỦ còn-nợ (hành vi cũ). STRING minor dương ≤ trần BIGINT (B1 no-float).
     */
    paidAmountMinor: positiveMinorString("Số tiền thực đóng").optional(),
    /**
     * US-E19.F1-0200 (@UC9296) — KHOÁ NGHIỆP VỤ chống DOUBLE-POST (SEC-DOUBLE-POST).
     *
     * Client CÓ THỂ gửi khoá ỔN ĐỊNH theo lần bấm nút (KHÔNG random mỗi lần retry) ⇒ bấm kép vì lag
     * ⇒ CÙNG khoá ⇒ ledger `unique(fundId, idempotencyKey)` chặn ⇒ ĐÚNG 1 bút toán.
     * VẮNG ⇒ server TỰ DERIVE khoá từ NỘI DUNG hành động (domain `deriveBusinessIdempotencyKey`) ⇒
     * client cũ (không biết field này) VẪN được chống double-post — KHÔNG regress.
     */
    businessIdempotencyKey: businessIdempotencyKeySchema.optional(),
  })
  .strict();

export type RecordPaidRequest = z.infer<typeof recordPaidRequestSchema>;

/**
 * POST /v1/member-dues/:id/record-paid — ghi nhận 1-đầu (AC1). member_due pending→paid, post
 * CONTRIBUTION (confirmationSource=admin_one_party). Idempotent qua Idempotency-Key + ledger unique.
 */
export const recordPaidResponseSchema = z
  .object({
    memberDueId: z.string().uuid(),
    ledgerTxId: z.string().uuid(),
    confirmationSource: z.literal(CONFIRMATION_SOURCE.ADMIN_ONE_PARTY),
    /** Số CONTRIBUTION posted lần này (partial ⇒ phần đóng; overpay ⇒ khớp còn-nợ due). */
    amountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    currency: z.string().length(3),
    /**
     * US-E16.F4-0185 (@UC9241/@UC9242): partial ⇒ `partially_paid`; đóng đủ/dư ⇒ `paid`.
     * (Hành vi cũ đóng-đủ vẫn trả `paid` — KHÔNG regress.)
     */
    newDueStatus: z.enum([MEMBER_DUE_STATUS.PAID, MEMBER_DUE_STATUS.PARTIALLY_PAID]),
    /** Tổng đã đóng tích luỹ cho due sau lần này (partial). STRING minor. */
    paidAmountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    /** Còn nợ sau lần này = amountMinor − paidAmountMinor (0 khi paid). STRING minor. */
    remainingMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    /** Phần dư đã ghi advance_credit (overpay). "0" nếu không dư. STRING minor. */
    advanceCreditMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    /** DebtEntry advance_credit id (producer E15.F3) nếu có overpay; null nếu không. */
    advanceCreditEntryId: z.string().uuid().nullable(),
    /**
     * US-E16.F6-0187 (@UC9249) — chính sách overpay HIỆU LỰC của quỹ (config `overpay_credit_policy`,
     * fund > group > system; default `credit` ⇒ KHÔNG regress F4). `reject` không bao giờ trả 200.
     */
    overpayPolicy: overpayCreditPolicySchema.default("credit"),
    /**
     * Phần dư CHỜ HOÀN khi policy=`refund` (KHÔNG vào quỹ, KHÔNG thành advance_credit). "0" ở policy
     * khác. STRING minor (B1).
     */
    refundPendingMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN).default("0"),
    recordedAt: z.string().datetime(),
  })
  .strict();

export type RecordPaidResponse = z.infer<typeof recordPaidResponseSchema>;

/**
 * POST /v1/collections/:id/dues/record-paid-all — bulk (AC2). Mỗi due chưa đóng = 1 tx idempotent.
 * Trả số ghi nhận + danh sách memberDueId đã paid.
 */
export const recordPaidBulkResponseSchema = z
  .object({
    recordedCount: z.number().int().nonnegative(),
    recordedMemberDueIds: z.array(z.string().uuid()),
  })
  .strict();

export type RecordPaidBulkResponse = z.infer<
  typeof recordPaidBulkResponseSchema
>;

/**
 * POST /v1/member-dues/:id/undo-record — huỷ ghi nhận (AC3) → REVERSAL append-only + due→pending.
 */
export const undoRecordResponseSchema = z
  .object({
    memberDueId: z.string().uuid(),
    reversalTxId: z.string().uuid(),
    newDueStatus: z.literal(MEMBER_DUE_STATUS.PENDING),
    undoneAt: z.string().datetime(),
  })
  .strict();

export type UndoRecordResponse = z.infer<typeof undoRecordResponseSchema>;

/* ===========================================================================
 * US-E10.F2-0076 — Gateway payment webhook (@UC0057) — @gate money,ledger.
 *
 * Schema GENERIC (provider-agnostic — VNPay/MoMo/ZaloPay adapter thật DEFER). Webhook = UNTRUSTED:
 * body CHỈ được tin SAU khi verify HMAC (constant-time) + timestamp window ở tầng verifier. Sau verify,
 * body parse qua schema này (validate biên C5). amountMinor = STRING minor unit (BigInt, KHÔNG float).
 * ========================================================================= */

/**
 * Payload webhook cổng thanh toán (generic). `.strip()` (mặc định zod) — bỏ field lạ provider gửi kèm
 * (KHÔNG .strict để tương thích nhiều provider), nhưng CÁC field lõi bắt buộc + đúng kiểu:
 *  - eventId: định danh sự kiện gateway — DÙNG LÀM khoá dedupe (idempotency chống replay). Non-empty.
 *  - memberDueId: khoản thu cần khớp (uuid).
 *  - amountMinor: số tiền đã thu (minor unit string) — service SO KHỚP với member_due.amountMinor.
 *  - currency: 3 ký tự — service SO KHỚP currency due.
 *  - paidAt: thời điểm thu (optional datetime — chỉ metadata, KHÔNG dùng cho dedupe/verify).
 */
export const gatewayWebhookRequestSchema = z.object({
  eventId: z.string().trim().min(1).max(200),
  memberDueId: z.string().uuid(),
  amountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
  currency: z.string().length(3),
  paidAt: z.string().datetime().optional(),
});

export type GatewayWebhookRequest = z.infer<typeof gatewayWebhookRequestSchema>;

/** Response webhook — cố ý TỐI GIẢN + ĐỒNG NHẤT (no-leak): chỉ báo đã nhận. KHÔNG lộ trạng thái ledger. */
export const gatewayWebhookResponseSchema = z
  .object({
    received: z.literal(true),
  })
  .strict();

export type GatewayWebhookResponse = z.infer<typeof gatewayWebhookResponseSchema>;
